import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Thermometer, ScanLine, Award, ArrowRight } from "lucide-react";
import InviteBusiness from "@/components/InviteBusiness";
import { useLanguage } from "@/i18n/LanguageContext";

const IMG_HERO = "https://images.pexels.com/photos/18586243/pexels-photo-18586243.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_STALLION = "https://images.pexels.com/photos/10479874/pexels-photo-10479874.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_HERITAGE = "https://images.unsplash.com/photo-1554755209-85e44182e019?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxoZXJpdGFnZSUyMHNoZWVwJTIwbGl2ZXN0b2NrJTIwZmFybSUyMHBhc3R1cmV8ZW58MHx8fHwxNzg3NzIzODQ5fDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { t } = useLanguage();
  return (
    <div>
      <section className="hero-grain text-stone-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border border-emerald-300/25 bg-emerald-950/30 px-3 py-1 rounded-full text-emerald-200 text-xs mono uppercase tracking-widest mb-6">
              <ShieldCheck className="h-3.5 w-3.5" /> {t("hero.badge")}
            </div>
            <h1 className="heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white">
              {t("hero.title1")}<br />
              <span className="text-[color:var(--earth-amber)]">{t("hero.title2")}</span><br />
              {t("hero.title3")}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-stone-200/85 max-w-xl">
              {t("hero.subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/marketplace"><Button data-testid="landing-browse-btn" className="btn-earth h-11 px-5">{t("hero.browse")} <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
              <Link to="/register"><Button data-testid="landing-register-btn" variant="outline" className="h-11 px-5 bg-transparent border-stone-300/40 text-stone-100 hover:bg-white/10">{t("hero.seller")}</Button></Link>
              <InviteBusiness size="lg" triggerClassName="h-11 px-5 bg-transparent border-stone-300/40 text-stone-100 hover:bg-white/10" />
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-xl">
              <Stat k="98.4%" v={t("hero.stat1")} />
              <Stat k="42" v={t("hero.stat2")} />
              <Stat k="LN₂" v={t("hero.stat3")} />
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
              <img src={IMG_HERO} alt="Champion cattle sire" className="w-full h-[440px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-white rounded-xl shadow-xl border border-stone-200 p-4 w-64">
              <div className="text-[10px] mono uppercase text-stone-500">Live escrow</div>
              <div className="heading text-lg text-stone-900 font-bold">ORD-A81CE9</div>
              <div className="mt-1 flex items-center gap-1.5 text-xs text-emerald-700 mono">● Escrow locked · LN₂ dispatched</div>
              <div className="mt-3 flex items-center justify-between text-xs text-stone-600">
                <span>Total</span><span className="mono font-semibold text-stone-900">$3,400.00</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-3 gap-6">
        <Feature icon={ShieldCheck} title={t("feat.1.t")} text={t("feat.1.x")} />
        <Feature icon={Thermometer} title={t("feat.2.t")} text={t("feat.2.x")} />
        <Feature icon={ScanLine} title={t("feat.3.t")} text={t("feat.3.x")} />
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16 grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <div className="inline-flex items-center gap-2 border border-amber-300/40 bg-amber-50 text-amber-700 px-3 py-1 rounded-full text-xs mono uppercase tracking-widest">
            <Award className="h-3.5 w-3.5" /> {t("heritage.badge")}
          </div>
          <h2 className="heading text-3xl sm:text-4xl font-bold mt-4 tracking-tight">{t("heritage.title")}</h2>
          <p className="mt-4 text-stone-600">
            {t("heritage.body")}
          </p>
          <div className="mt-6 flex gap-3">
            <Link to="/heritage"><Button data-testid="landing-heritage-btn" className="btn-forest h-10 px-4">{t("heritage.cta")}</Button></Link>
          </div>
        </div>
        <div className="lg:col-span-7 order-1 lg:order-2 grid grid-cols-2 gap-4">
          <img src={IMG_HERITAGE} className="rounded-2xl h-72 w-full object-cover shadow-lg" alt="Heritage sheep" />
          <img src={IMG_STALLION} className="rounded-2xl h-72 w-full object-cover shadow-lg mt-8" alt="Heritage stallion" />
        </div>
      </section>
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div>
      <div className="heading text-2xl font-bold text-white mono">{k}</div>
      <div className="text-xs text-stone-300/80 uppercase tracking-widest mt-1">{v}</div>
    </div>
  );
}
function Feature({ icon: Icon, title, text }) {
  return (
    <div className="surface-card p-6">
      <div className="h-11 w-11 rounded-lg bg-[color:var(--sage-subtle)] text-[color:var(--forest-deep)] grid place-items-center">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="heading text-lg font-semibold mt-4">{title}</h3>
      <p className="mt-2 text-sm text-stone-600">{text}</p>
    </div>
  );
}
